import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api, getApiBaseUrl } from '../../api/client';
import { checkPermission } from '../../utils/permissionUtils';
import { useBranding } from '../../context/BrandingContext';
import { getSchemeColors } from '../../utils/themeColors';
import '../../App.css';
import Button from '../ui/Button';
import MultiSelect, { MultiSelectOption, SelectId } from '../MultiSelect';
import AssignmentField from './AssignmentField';
import type { AssignmentPerson } from '../AssignmentDrawer';
import ToggleSwitch from './ToggleSwitch';
import { useSeedEffect, type SeedKey } from '../../hooks/useSeedEffect';
import DateTimePicker from '../DateTimePicker';
import FormField from '../ui/FormField';
import MarkdownEditor from '../MarkdownEditor';
import NumericInput from '../NumericInput';
import ActivityPicker from './ActivityPicker';
import CoursePicker from './CoursePicker';
import { validate, required } from '../../utils/validation';
import Modal from '../Modal';
import EventPaymentQR from '../EventPaymentQR';
import { translateError } from '../../utils/errorMessages';

/**
 * Get the full URL for an image.
 * If the URL is relative (starts with /), prepend the API base URL.
 */
const getImageUrl = (url: string): string => {
  if (url.startsWith('/')) {
    return `${getApiBaseUrl()}${url}`;
  }
  return url;
};

interface ISelectOption {
  _id: string | number;
  name: string;
  /**
   * Member option only. `GET /api/members` returns each member's own groups;
   * the assignment field uses them to show whether someone is on the roster
   * directly or via an assigned group.
   */
  groups?: Array<{ _id: string | number; name: string }>;
  role?: {
    type?: string;
    name: string;
    permissions?: Record<string, number>;
  };
}

export interface IEventFormData {
  name: string;
  description: string;
  location?: string;
  startTime: string;
  endTime: string;
  members: SelectId[];
  /** Seed-only: saved roster by name, incl. people outside the viewer's scope. Never submitted. */
  rosterPeople?: AssignmentPerson[];
  groups: SelectId[];
  coveredByMembershipIds: SelectId[];
  activityId: number | null;
  courseId: number | null;
  defaultAttendance: boolean | null;
  responsible: string | null;
  price: number;
  isOpen?: boolean;
  capacity?: number;
  publicSlug?: string;
  private?: boolean;
  extendedDescription?: string;
  gallery?: string[];
  colorSettings?: {
    primaryColor?: string;
    secondaryColor?: string;
  };
}

interface IEventFormProps {
  initialData?: Partial<IEventFormData>;
  /** Record identity — seeds once per record instead of per object identity. */
  seedKey?: SeedKey;
  /**
   * Reports whether the form differs from the seed. Lets a host modal refuse a
   * stray Escape/backdrop click that would throw the whole edit away.
   */
  onDirtyChange?: (dirty: boolean) => void;
  onSubmit: (data: IEventFormData) => Promise<void>;
  loading?: boolean;
  submitButtonText: string;
  /** Optional ID prefix to avoid duplicate IDs when used in modals */
  idPrefix?: string;
  /** Optional delete handler - if provided, a delete button will be shown */
  onDelete?: () => void;
  /** Optional delete button text */
  deleteButtonText?: string;
  /** Optional pre-fetched members to avoid redundant API calls */
  prefetchedMembers?: ISelectOption[];
  /** Optional pre-fetched groups to avoid redundant API calls */
  prefetchedGroups?: ISelectOption[];
  /** Optional pre-fetched admins to avoid redundant API calls */
  prefetchedAdmins?: ISelectOption[];
  /** Scoped admin's allowed activity ids (from useAuth/accessScope). */
  allowedActivityIds?: number[];
  /** Optional event ID for existing events (enables payment QR button) */
  eventId?: string;
}

const EventForm: React.FC<IEventFormProps> = ({
  initialData,
  seedKey,
  onDirtyChange,
  onSubmit,
  loading = false,
  submitButtonText,
  idPrefix = '',
  onDelete,
  deleteButtonText,
  prefetchedMembers,
  prefetchedGroups,
  prefetchedAdmins,
  allowedActivityIds,
  eventId,
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [location, setLocation] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [members, setMembers] = useState<SelectId[]>([]);
  const [rosterPeople, setRosterPeople] = useState<AssignmentPerson[]>([]);
  const [groups, setGroups] = useState<SelectId[]>([]);
  // Labels as last seeded (= saved); new ones get applied by the server on save.
  const [savedGroups, setSavedGroups] = useState<SelectId[]>([]);
  const [coveredByMembershipIds, setCoveredByMembershipIds] = useState<SelectId[]>([]);
  const [showAllMemberships, setShowAllMemberships] = useState(false);
  const [activityId, setActivityId] = useState<number | null>(null);
  const [courseId, setCourseId] = useState<number | null>(null);
  const [defaultAttendance, setDefaultAttendance] = useState<boolean | null>(null);
  const [responsible, setResponsible] = useState<string>('');
  const [price, setPrice] = useState<number>(0);
  const [isOpen, setIsOpen] = useState(false);
  const [capacity, setCapacity] = useState<number>(0);
  const [publicSlug, setPublicSlug] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [extendedDescription, setExtendedDescription] = useState('');
  const [gallery, setGallery] = useState<string[]>([]);
  const [galleryUploading, setGalleryUploading] = useState(false);
  const [galleryError, setGalleryError] = useState<string | null>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const { colorScheme } = useBranding();
  const schemeDefaults = useMemo(() => getSchemeColors(colorScheme), [colorScheme]);
  const [primaryColor, setPrimaryColor] = useState(schemeDefaults.primary);
  const [secondaryColor, setSecondaryColor] = useState(schemeDefaults.light);

  const [allMembers, setAllMembers] = useState<ISelectOption[]>([]);
  const [allGroups, setAllGroups] = useState<ISelectOption[]>([]);
  const [admins, setAdmins] = useState<ISelectOption[]>([]);
  const [allMemberships, setAllMemberships] = useState<Array<{ _id: number; name: string; activities?: Array<{ _id: number }> }>>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showQRModal, setShowQRModal] = useState(false);

  const [optionsLoading, setOptionsLoading] = useState(true);
  const [seedGen, setSeedGen] = useState(0);
  const onDirtyChangeRef = useRef(onDirtyChange);
  onDirtyChangeRef.current = onDirtyChange;
  const navigate = useNavigate();

  // Determine if we need to fetch options
  const hasPrefetchedOptions = prefetchedMembers && prefetchedGroups && prefetchedAdmins;

  // Fetch all members, groups and users (for responsible selection)
  useEffect(() => {
    if (hasPrefetchedOptions) {
      setAllMembers(prefetchedMembers);
      setAllGroups(prefetchedGroups);
      setAdmins(prefetchedAdmins);
      setOptionsLoading(false);
      return;
    }

    const fetchSelectOptions = async () => {
      try {
        const { data: membersData } = await api.members.list();
        const { data: groupsData } = await api.groups.list();
        const { data: usersData } = await api.users.list();
        const { data: membershipsData } = await api.memberships.list();

        setAllMembers(membersData as ISelectOption[]);
        setAllGroups(groupsData as ISelectOption[]);
        setAllMemberships(membershipsData as Array<{ _id: number; name: string; activities?: Array<{ _id: number }> }>);
        // Filter users who can manage events (Admin/Owner role or events:write permission)
        setAdmins((usersData as ISelectOption[]).filter(u =>
          u.role?.type === 'admin' ||
          u.role?.name === 'Owner' ||
          checkPermission(u.role?.permissions, 'events:write')
        ));
        setOptionsLoading(false);
      } catch (err: any) {
        setOptionsLoading(false);
        if (err.response && err.response.status === 401) {
          navigate('/login');
        }
      }
    };
    fetchSelectOptions();
  }, [navigate, hasPrefetchedOptions, prefetchedMembers, prefetchedGroups, prefetchedAdmins]);

  // Seeded once per record (seedKey), else per initialData identity.
  useSeedEffect(initialData, seedKey, (seed) => {
    if (seed) {

      setName(seed.name || '');
      setDescription(seed.description || '');
      setLocation(seed.location || '');
      setStartTime(seed.startTime || '');
      setEndTime(seed.endTime || '');
      setMembers(seed.members || []);
      setRosterPeople(seed.rosterPeople || []);
      setGroups(seed.groups || []);
      setSavedGroups(seed.groups || []);
      setCoveredByMembershipIds(seed.coveredByMembershipIds || []);
      setActivityId(seed.activityId ?? null);
      setCourseId(seed.courseId ?? null);
      setDefaultAttendance(seed.defaultAttendance ?? null);
      setResponsible(seed.responsible || '');
      setPrice(seed.price || 0);
      setIsOpen(seed.isOpen || false);
      setCapacity(seed.capacity || 0);
      setPublicSlug(seed.publicSlug || '');
      setIsPrivate(seed.private || false);
      setExtendedDescription(seed.extendedDescription || '');
      setGallery(seed.gallery || []);
      if (seed.colorSettings) {
        setPrimaryColor(seed.colorSettings.primaryColor || schemeDefaults.primary);
        setSecondaryColor(seed.colorSettings.secondaryColor || schemeDefaults.light);
      }
    }
    setSeedGen((g) => g + 1);
  });

  const handleGalleryUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setGalleryError(t('events.galleryInvalidType') || 'Only PNG, JPG, and WebP files are allowed');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setGalleryError(t('events.galleryFileTooLarge') || 'File size must be less than 5MB');
      return;
    }

    setGalleryError(null);
    setGalleryUploading(true);

    try {
      const { data } = await api.uploads.galleryImage(file);
      setGallery([...gallery, data.url]);
    } catch (err: any) {
      setGalleryError(translateError(err, t, 'events.galleryUploadFailed') || 'Failed to upload image');
    } finally {
      setGalleryUploading(false);
      // Reset file input
      if (galleryInputRef.current) {
        galleryInputRef.current.value = '';
      }
    }
  };

  const removeImage = async (index: number) => {
    const url = gallery[index];

    // Only delete from server if it's a server-stored image (starts with /)
    if (url.startsWith('/')) {
      try {
        await api.uploads.deleteGalleryImage(url);
      } catch (err) {
        // Continue with removal even if delete fails
        console.error('Failed to delete image from server:', err);
      }
    }

    const newGallery = [...gallery];
    newGallery.splice(index, 1);
    setGallery(newGallery);
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    const nameError = validate(name, [required(t('validation.nameRequired'))]);
    if (nameError) newErrors.name = nameError;

    const startTimeError = validate(startTime, [required(t('validation.startTimeRequired'))]);
    if (startTimeError) newErrors.startTime = startTimeError;

    const endTimeError = validate(endTime, [required(t('validation.endTimeRequired'))]);
    if (endTimeError) newErrors.endTime = endTimeError;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const formData: IEventFormData = useMemo(() => ({
    name,
    description,
    location: location || undefined,
    startTime,
    endTime,
    members,
    groups,
    coveredByMembershipIds,
    activityId,
    courseId,
    defaultAttendance,
    responsible: responsible || null,
    price,
    isOpen,
    capacity: capacity || undefined,
    publicSlug: publicSlug || undefined,
    private: isPrivate,
    extendedDescription,
    gallery,
    colorSettings: {
      primaryColor,
      secondaryColor,
    },
  }), [
    name, description, location, startTime, endTime, members, groups,
    coveredByMembershipIds, activityId, courseId, defaultAttendance, responsible,
    price, isOpen, capacity, publicSlug, isPrivate, extendedDescription, gallery,
    primaryColor, secondaryColor,
  ]);

  // Dirty = payload differs from the one captured right after the last seed.
  // The baseline is re-taken per seed generation, so a re-seed never reports dirty.
  const snapshot = useMemo(() => JSON.stringify(formData), [formData]);
  const baselineRef = useRef<string | null>(null);
  const baselineGenRef = useRef(-1);
  const dirtyRef = useRef(false);

  useEffect(() => {
    if (baselineGenRef.current !== seedGen) {
      baselineGenRef.current = seedGen;
      baselineRef.current = snapshot;
    }
    const dirty = snapshot !== baselineRef.current;
    if (dirty !== dirtyRef.current) {
      dirtyRef.current = dirty;
      onDirtyChangeRef.current?.(dirty);
    }
  }, [snapshot, seedGen]);

  const submitHandler = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    onSubmit(formData);
  };

  // Generate unique IDs for form elements
  const getId = (baseId: string) => idPrefix ? `${idPrefix}-${baseId}` : baseId;

  if (optionsLoading) {
    return <p>{t('common.loading')}</p>;
  }

  return (
    <form onSubmit={submitHandler} className="form-container">
      {/* Basic Info Section */}
      <div className="form-section">
        <h4 className="form-section__title">{t('events.sectionBasicInfo') || 'Basic Info'}</h4>
        <FormField
          htmlFor={getId('name')}
          label={t('common.name')}
          labelHelpText={t('events.nameHelp')}
          error={errors.name}
          required
        >
          <input
            type="text"
            id={getId('name')}
            placeholder={t('events.enterName')}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (errors.name) setErrors(prev => ({ ...prev, name: '' }));
            }}
          />
        </FormField>
        <FormField
          htmlFor={getId('description')}
          label={t('common.description')}
          labelHelpText={t('events.descriptionHelp')}
        >
          <textarea
            id={getId('description')}
            placeholder={t('events.enterDescription')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
          ></textarea>
        </FormField>
        <FormField
          htmlFor={getId('location')}
          label={t('events.location')}
        >
          <input
            type="text"
            id={getId('location')}
            placeholder={t('events.enterLocation')}
            value={location}
            onChange={(e) => setLocation(e.target.value)}
          />
        </FormField>
      </div>

      {/* Schedule Section */}
      <div className="form-section">
        <h4 className="form-section__title">{t('events.sectionSchedule') || 'Schedule'}</h4>
        <div className="form-grid">
          <DateTimePicker
            id={getId('startTime')}
            label={t('events.startTime')}
            value={startTime}
            onChange={(value) => {
              setStartTime(value);
              if (errors.startTime) setErrors(prev => ({ ...prev, startTime: '' }));
            }}
            required
            error={errors.startTime}
          />
          <DateTimePicker
            id={getId('endTime')}
            label={t('events.endTime')}
            value={endTime}
            onChange={(value) => {
              setEndTime(value);
              if (errors.endTime) setErrors(prev => ({ ...prev, endTime: '' }));
            }}
            required
            error={errors.endTime}
          />
        </div>
      </div>

      {/* Assignments Section */}
      <div className="form-section">
        <h4 className="form-section__title">{t('events.sectionAssignments') || 'Assignments'}</h4>
        <ActivityPicker
          value={activityId}
          onChange={setActivityId}
          required={!!(allowedActivityIds && allowedActivityIds.length > 0)}
          allowedActivityIds={allowedActivityIds}
          label={t('events.activity')}
          helpText={t('events.activityHelp')}
        />
        <CoursePicker
          value={courseId}
          activityId={activityId}
          onChange={setCourseId}
          label={t('events.course')}
        />
        {/* One roster control; picking a group copies its members and keeps it as a label. */}
        <AssignmentField
          id={getId('assignment')}
          label={t('events.assignMembers')}
          drawerTitle={name ? `${t('events.assignMembers')} — ${name}` : t('events.assignMembers')}
          people={allMembers}
          rosterPeople={rosterPeople}
          groups={allGroups}
          memberIds={members}
          groupIds={groups}
          savedGroupIds={savedGroups}
          onChange={(next) => {
            setMembers(next.memberIds);
            setGroups(next.groupIds);
          }}
        />
        {/*
          Coverage memberships picker — by default we filter to memberships
          linked to this event's activity (via membership_activities), with a
          "Show all" toggle as the escape hatch for cross-activity edge cases.
          When the event has no activityId, show all by default.
        */}
        {(() => {
          const activityFilter = !showAllMemberships && activityId != null;
          const filtered = activityFilter
            ? allMemberships.filter(m => (m.activities || []).some(a => a._id === activityId))
            : allMemberships;
          const options: MultiSelectOption[] = filtered.map(m => ({ _id: m._id, name: m.name }));
          // Always include selected memberships even if filtered out so the
          // user can still see what's selected.
          for (const sid of coveredByMembershipIds) {
            if (!options.some(o => String(o._id) === String(sid))) {
              const found = allMemberships.find(m => String(m._id) === String(sid));
              if (found) options.push({ _id: found._id, name: found.name });
            }
          }
          return (
            <>
              <MultiSelect
                id={getId('coveredBy')}
                label={t('events.coveredBy')}
                labelHelpText={t('events.coveredByHelp')}
                options={options}
                selectedIds={coveredByMembershipIds}
                onChange={setCoveredByMembershipIds}
                placeholder={t('events.selectCoveredBy')}
              />
              {activityId != null && (
                <div className="form-group" style={{ marginTop: '-0.5rem' }}>
                  <ToggleSwitch
                    checked={showAllMemberships}
                    onChange={setShowAllMemberships}
                    label={t('events.showAllMemberships')}
                  />
                </div>
              )}
            </>
          );
        })()}
        <div className="form-group">
          <label htmlFor={getId('responsible')}>{t('events.responsible')}</label>
          <select
            id={getId('responsible')}
            value={responsible}
            onChange={(e) => setResponsible(e.target.value)}
          >
            <option value="">{t('common.select')}</option>
            {admins.map((admin) => (
              <option key={admin._id} value={admin._id}>
                {admin.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Settings Section */}
      <div className="form-section">
        <h4 className="form-section__title">{t('events.sectionSettings') || 'Settings'}</h4>
        <div className="form-grid">
          <div className="form-group">
            <label htmlFor={getId('defaultAttendance')}>{t('events.defaultAttendance')}</label>
            <select
              id={getId('defaultAttendance')}
              value={defaultAttendance === true ? 'yes' : defaultAttendance === false ? 'no' : 'null'}
              onChange={(e) => {
                const val = e.target.value;
                setDefaultAttendance(val === 'yes' ? true : val === 'no' ? false : null);
              }}
            >
              <option value="null">{t('common.maybe')} (?)</option>
              <option value="yes">{t('common.yes')}</option>
              <option value="no">{t('common.no')}</option>
            </select>
          </div>
          <div className="form-group">
            <label htmlFor={getId('price')}>{t('events.price')}</label>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <NumericInput
                id={getId('price')}
                min="0"
                step="1"
                value={price === 0 ? '' : price}
                onChange={(e) => setPrice(e.target.value === '' ? 0 : parseFloat(e.target.value))}
                style={{ flex: 1 }}
              />
              {eventId && price > 0 && (
                <button
                  type="button"
                  onClick={() => setShowQRModal(true)}
                  title={t('events.showPaymentQR')}
                  style={{
                    background: 'none',
                    border: '1px solid var(--border-color)',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    padding: '0.4rem 0.5rem',
                    fontSize: '1.1rem',
                    lineHeight: 1,
                    color: 'var(--text-color)',
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="2" width="8" height="8" rx="1" />
                    <rect x="14" y="2" width="8" height="8" rx="1" />
                    <rect x="2" y="14" width="8" height="8" rx="1" />
                    <rect x="14" y="14" width="4" height="4" />
                    <line x1="22" y1="14" x2="22" y2="18" />
                    <line x1="14" y1="22" x2="18" y2="22" />
                    <line x1="22" y1="22" x2="22" y2="22" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="form-grid" style={{ marginTop: '0.5rem' }}>
          <div className="form-group">
            <ToggleSwitch
              id={getId('private')}
              checked={isPrivate}
              onChange={setIsPrivate}
              label={t('events.privateEvent')}
            />
          </div>
          <div className="form-group">
            <ToggleSwitch
              id={getId('isOpen')}
              checked={isOpen}
              onChange={setIsOpen}
              label={t('events.isOpenEvent')}
            />
          </div>
        </div>
      </div>

      {/* Open Event Settings Section */}
      {isOpen && (
        <div className="form-section">
          <h4 className="form-section__title">{t('events.sectionPublicEvent') || 'Public Event Settings'}</h4>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor={getId('capacity')}>{t('events.capacity')}</label>
              <NumericInput
                id={getId('capacity')}
                min="0"
                value={capacity}
                onChange={(e) => setCapacity(parseInt(e.target.value) || 0)}
              />
            </div>
            <div className="form-group">
              <label htmlFor={getId('publicSlug')}>{t('events.publicSlug')}</label>
              <input
                type="text"
                id={getId('publicSlug')}
                placeholder="e.g. summer-camp-2025"
                value={publicSlug}
                onChange={(e) => setPublicSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
              />
            </div>
          </div>
          {publicSlug && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
              <small style={{ color: 'var(--text-muted)' }}>
                Public Link: {window.location.origin}/e/{publicSlug}
              </small>
              <Button
                size="sm"
                onClick={() => {
                  navigator.clipboard.writeText(`${window.location.origin}/e/${publicSlug}`);
                  alert('Link copied to clipboard!');
                }}
              >
                {t('common.copy') || 'Copy'}
              </Button>
            </div>
          )}

          <div className="form-group">
            <label htmlFor={getId('extendedDescription')}>{t('events.extendedDescription') || 'Extended Description'}</label>
            <MarkdownEditor
              id={getId('extendedDescription')}
              placeholder={t('events.enterExtendedDescription') || 'Enter detailed information for the public page...'}
              value={extendedDescription}
              onChange={setExtendedDescription}
              rows={8}
            />
          </div>

          <div className="form-group">
            <label>{t('events.colorSettings') || 'Color Theme'}</label>
            <div style={{ display: 'flex', gap: '2rem', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <label style={{ fontSize: '0.8rem', margin: 0 }}>{t('events.primaryColor') || 'Primary'}</label>
                <input
                  type="color"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  style={{ width: '40px', height: '30px', padding: 0, border: 'none' }}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <label style={{ fontSize: '0.8rem', margin: 0 }}>{t('events.secondaryColor') || 'Secondary'}</label>
                <input
                  type="color"
                  value={secondaryColor}
                  onChange={(e) => setSecondaryColor(e.target.value)}
                  style={{ width: '40px', height: '30px', padding: 0, border: 'none' }}
                />
              </div>
            </div>
          </div>

          <div className="form-group">
            <label>{t('events.gallery') || 'Image Gallery'}</label>
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', alignItems: 'center' }}>
              <input
                ref={galleryInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={handleGalleryUpload}
                disabled={galleryUploading}
                style={{ flex: 1 }}
                aria-label={t('events.uploadGalleryImage') || 'Upload gallery image'}
              />
              {galleryUploading && (
                <span style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                  {t('common.uploading') || 'Uploading...'}
                </span>
              )}
            </div>
            {galleryError && (
              <div style={{ color: 'var(--error-color)', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                {galleryError}
              </div>
            )}
            <small style={{ color: 'var(--text-muted)', display: 'block', marginBottom: '0.5rem' }}>
              {t('events.galleryHint') || 'PNG, JPG, or WebP. Max 5MB per image.'}
            </small>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: '0.5rem' }}>
              {gallery.map((url, index) => (
                <div key={index} style={{ position: 'relative', border: '1px solid var(--border-color)', borderRadius: '4px', overflow: 'hidden', aspectRatio: '1' }}>
                  <img
                    src={getImageUrl(url)}
                    alt={`Gallery ${index + 1}`}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => removeImage(index)}
                    style={{ position: 'absolute', top: '2px', right: '2px', background: 'rgba(255,0,0,0.7)', color: 'white', border: 'none', borderRadius: '50%', width: '20px', height: '20px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px' }}
                    aria-label={t('events.removeImage') || 'Remove image'}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {eventId && price > 0 && (
        <Modal
          isOpen={showQRModal}
          onClose={() => setShowQRModal(false)}
          title={t('events.paymentQRTitle')}
          size="small"
        >
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem', padding: '1rem 0' }}>
            <EventPaymentQR eventId={eventId} size={200} />
            <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', textAlign: 'center', margin: 0 }}>
              {t('events.paymentQRDescription')}
            </p>
          </div>
        </Modal>
      )}

      {onDelete ? (
        <div className="form-actions">
          <Button type="submit" fullWidth loading={loading}>
            {submitButtonText}
          </Button>
          <Button
            type="button"
            variant="danger"
            fullWidth
            onClick={onDelete}
          >
            {deleteButtonText || t('common.delete')}
          </Button>
        </div>
      ) : (
        <Button type="submit" fullWidth loading={loading}>
          {submitButtonText}
        </Button>
      )}
    </form>
  );
};

export default EventForm;
